import React, { useState } from "react";

import { useDeleteFarm, useTranslation } from "utils";

import { Modal, Message as message } from "components";

type DeleteFarmModalProps = {
  farm: { id: string; name: string } | null;
  onClose: () => void;
  onDeleted: (farmId: string) => void;
};

const DeleteFarmDialog = ({ farm, onClose, onDeleted }: DeleteFarmModalProps) => {
  const { t } = useTranslation(["farm", "global"]);
  const { mutate: deleteFarm, isPending } = useDeleteFarm();
  const [password, setPassword] = useState("");

  const handleDelete = () => {
    if (!farm) return;
    if (!password) {
      message({ content: t("global:errors.mandatoryFields"), icon: "error" });
      return;
    }

    deleteFarm(
      { password, farmId: farm.id },
      {
        onSuccess: () => {
          message({ content: t("farm:farmDelete.deleted", { deletedFarm: farm.name }), icon: "tick" });
          onDeleted(farm.id);
          onClose();
        },
        onError: () => {
          message({ content: t("farm:farmDelete.error.delete", { deletedFarm: farm.name }), icon: "error" });
        },
      }
    );
  };

  return (
    <Modal
      dataTestId="delete-farm-modal"
      title={t("farm:farmDelete.title")}
      description={t("farm:farmDelete.description")}
      inputType="password"
      input={{
        initialValue: "",
        name: "password",
        rules: [{ required: true }],
        label: t("farm:farmDelete.password"),
        onChange: (event) => setPassword(event.target.value),
      }}
      okText={t("farm:farmDelete.confirm")}
      okButtonProps={{ "data-testid": "confirm-delete-button" }}
      cancelText={t("farm:farmDelete.cancel")}
      onCancel={onClose}
      onOk={handleDelete}
      showModal={!!farm}
      loading={isPending}
    />
  );
};

/**
 * A fresh dialog on every opening, so a password typed before never confirms a later deletion.
 * The dialog stays while it closes, so its fade-out plays.
 */
export const DeleteFarmModal = ({ farm, onClose, onDeleted }: DeleteFarmModalProps) => {
  const [opening, setOpening] = useState(0);
  const [shownFarmId, setShownFarmId] = useState<string | null>(farm?.id ?? null);

  if ((farm?.id ?? null) !== shownFarmId) {
    setShownFarmId(farm?.id ?? null);
    if (farm) setOpening((count) => count + 1);
  }

  return <DeleteFarmDialog key={opening} farm={farm} onClose={onClose} onDeleted={onDeleted} />;
};
